import axios from 'axios';

// API base URL — auto-resolves based on environment:
//  1. Explicit override via REACT_APP_API_URL (recommended for prod)
//  2. Local dev → http://localhost:5000/api
//  3. Production / deployed → same origin + /api (works with reverse proxy)
const BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'development' ? '/api' : `${window.location.origin}/api`
);

// PictureAsPdfIcon for payslip download uses this directly — expose it
export const API_BASE_URL = BASE_URL;

const api = axios.create({ baseURL: BASE_URL, timeout: 60000 });

// ── JWT auth helpers ────────────────────────────────────────────────────────
const AUTH_KEY = 'payroll.auth';
const ACTIVE_CONTEXT_KEY = 'payroll.activeContext';

export const getAuth = () => {
  try { return JSON.parse(localStorage.getItem(AUTH_KEY) || 'null'); }
  catch { return null; }
};
export const setAuth = (auth) => {
  if (auth) localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
  else      localStorage.removeItem(AUTH_KEY);
};
export const clearAuth = () => localStorage.removeItem(AUTH_KEY);

export const getActivePayrollContext = () => {
  try { return JSON.parse(localStorage.getItem(ACTIVE_CONTEXT_KEY) || 'null'); }
  catch { return null; }
};

// Attach Bearer token to every outgoing request when present.
api.interceptors.request.use((config) => {
  const auth = getAuth();
  if (auth?.token) {
    config.headers = config.headers || {};
    config.headers.Authorization = 'Bearer ' + auth.token;
  }
  
  // Disable browser cache for all API requests to ensure fresh data
  config.headers = config.headers || {};
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';

  const activeContext = getActivePayrollContext();
  if (activeContext?.company_id) {
    config.headers = config.headers || {};
    config.params = { ...(config.params || {}) };
    if (!config.params.active_company_id) config.params.active_company_id = activeContext.company_id;
    if (activeContext.bg_id && !config.params.active_bg_id)           config.params.active_bg_id      = activeContext.bg_id;
    if (activeContext.ldg_id && !config.params.active_ldg_id)         config.params.active_ldg_id     = activeContext.ldg_id;
    if (activeContext.payroll_id && !config.params.active_payroll_id) config.params.active_payroll_id = activeContext.payroll_id;
    config.headers['X-Active-Company-Id'] = config.params.active_company_id;
    if (activeContext.bg_id && !config.headers['X-Active-Bg-Id'])           config.headers['X-Active-Bg-Id']      = activeContext.bg_id;
    if (activeContext.ldg_id && !config.headers['X-Active-Ldg-Id'])         config.headers['X-Active-Ldg-Id']     = activeContext.ldg_id;
    if (activeContext.payroll_id && !config.headers['X-Active-Payroll-Id']) config.headers['X-Active-Payroll-Id'] = activeContext.payroll_id;
  }
  return config;
});

// Auth API
export const login          = (username, password) => api.post('/auth/login', { username, password });
export const fetchMe        = ()                    => api.get('/auth/me');
export const changePassword = (current_password, new_password) =>
  api.post('/auth/change-password', { current_password, new_password });

// Response interceptor — attach friendly user-facing message to error, preserve original axios shape.
// Backend now returns { success: false, code, message, meta? } with proper HTTP status.
// We enrich (not replace) the axios error so existing `err.response?.data?.message` paths still work,
// while new code can use `err.userMessage` / `err.code` for cleaner display.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Network error / server unreachable — no response object
    if (!error.response) {
      error.userMessage = 'Cannot reach server. Check your internet connection.';
      error.code = 'NETWORK_ERROR';
      error.userFriendly = true;
      return Promise.reject(error);
    }
    const { status, data } = error.response;
    const backendMsg  = data?.message;
    const backendCode = data?.code;

    // 401 from a NON-login route → token expired / invalid. Auto-logout and
    // bounce back to login page so the user re-authenticates.
    const isLoginRoute = (error.config?.url || '').includes('/auth/login');
    if (status === 401 && !isLoginRoute) {
      clearAuth();
      // Avoid infinite redirect loop if already on login
      if (typeof window !== 'undefined' && !window.location.pathname.includes('login')) {
        // Use a short delay so the in-flight error can still display
        setTimeout(() => { window.location.href = '/'; }, 100);
      }
    }

    // Map status → friendly message
    let msg = backendMsg;
    let code = backendCode;
    if (!msg) {
      if (status === 413) { msg = 'Upload too large. Compress your file and try again.'; code = code || 'PAYLOAD_TOO_LARGE'; }
      else if (status === 409) { msg = 'Record already exists with this value.';        code = code || 'CONFLICT'; }
      else if (status === 404) { msg = 'Record not found.';                              code = code || 'NOT_FOUND'; }
      else if (status === 401) { msg = 'Session expired. Please log in again.';          code = code || 'UNAUTHORIZED'; }
      else if (status === 400) { msg = 'Invalid request. Check the highlighted fields.'; code = code || 'BAD_REQUEST'; }
      else if (status >= 500)  { msg = 'Server error. Please try again or contact support.'; code = code || 'SERVER_ERROR'; }
      else                     { msg = error.message || 'Unknown error';                 code = code || 'UNKNOWN'; }
    }
    error.userMessage  = msg;
    error.code         = code;
    error.userFriendly = true;
    return Promise.reject(error);
  }
);

export const fetchTables = (params = {}) => api.get('/tables', { params });
export const fetchRecords = (table, params = {}) => api.get(`/${table.toLowerCase()}`, { params });
export const fetchRecord = (table, id, params = {}) => api.get(`/${table.toLowerCase()}/${id}`, { params });
export const createRecord = (table, data) => api.post(`/${table.toLowerCase()}`, data);
export const updateRecord = (table, id, data) => api.put(`/${table.toLowerCase()}/${id}`, data);
export const deleteRecord = (table, id) => api.delete(`/${table.toLowerCase()}/${id}`);
export const fetchNextEmployeeCode = (company_id) => api.get('/assignment/next-employee-code', { params: { company_id } });
export const generatePayPeriods = (payroll_id) => api.post('/generate-pay-periods', { payroll_id });
export const fetchWorkingDays   = (year, month, payroll_id) =>
  api.get('/working-days', { params: { year, month, payroll_id } });

// Salary structure (component) engine
export const createElementType       = (payload) => api.post('/element-type/create', payload);
export const updateElementType       = (id, payload) => api.put('/element-type/update/' + id, payload);
export const fetchElementDetail      = (id) => api.get('/element-type/' + id + '/detail');
export const evaluateFormula         = (expression, variables) => api.post('/formula/evaluate', { expression, variables });
export const fetchElementSetupLookups = () => api.get('/element-setup/lookups');

// Payroll Run Engine
export const executePayrollRun       = (id, body = {}) => api.post(`/payroll-run/${id}/execute`, body);
export const approvePayrollRun       = (id, approved_by) => api.post(`/payroll-run/${id}/approve`, { approved_by });
export const generatePayslipsForRun  = (id) => api.post(`/payroll-run/${id}/generate-payslips`);
export const createPaymentsForRun    = (id) => api.post(`/payroll-run/${id}/create-payments`);
export const generateBankFileForRun  = (id) => api.post(`/payroll-run/${id}/generate-bank-file`);
export const reversePayrollRun       = (id) => api.post(`/payroll-run/${id}/reverse`);
export const fetchOvertimeData      = (id) => api.get(`/payroll-run/${id}/overtime_data`);

// Statutory Configuration
export const fetchStatutoryConfig  = (company_id) =>
  api.get('/statutory-config', { params: { company_id } });
export const saveStatutoryConfig   = (payload) =>
  api.post('/statutory-config', payload);
export const deleteStatutoryConfig = (company_id, component_code) =>
  api.delete('/statutory-config', { params: { company_id, component_code } });

// Salary Templates
export const fetchSalaryTemplates = ()        => api.get('/salary-templates');
export const fetchSalaryTemplate  = (id)      => api.get('/salary-templates/' + id);
export const createSalaryTemplate = (payload) => api.post('/salary-templates', payload);
export const updateSalaryTemplate = (id, payload) => api.put('/salary-templates/' + id, payload);
export const toggleSalaryTemplateStatus = (id, status) => api.put(`/salary-templates/${id}/toggle-status`, { status });
export const deleteSalaryTemplate = (id)      => api.delete('/salary-templates/' + id);

// Full & Final Settlement
export const fetchFfDefaults = (assignment_id) => api.get('/full_final_settlement/calculate-defaults', { params: { assignment_id } });
export const approveFfSettlement = (id) => api.post(`/full_final_settlement/${id}/approve`);
export const recordFfClearance = (id, payload) => api.post(`/full_final_settlement/${id}/record-clearance`, payload);
export const recordFfPayment = (id, payload) => api.post(`/full_final_settlement/${id}/record-payment`, payload);

// Advance Salary Request Approvers
export const fetchApprovers = (id) => api.get(`/advance_salary_request/${id}/approvers`);

// Form 16 Summary Defaults
export const fetchForm16Defaults = (assignment_id, financial_year) =>
  api.get('/form16_summary/calculate-defaults', { params: { assignment_id, financial_year } });

export default api;
